import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import useThemeColor from "../../../hooks/useThemeColor";
import MUITimePicker from "../MUITimePicker/MUITimePicker";
import FormInput from "../Input/formInput";
import "react-datetime/css/react-datetime.css";
import "./scheduleStyle.css";
import "../MUITimePicker/MUITimePicker.css";

const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEKDAY_INDEXES = [0, 1, 2, 3, 4];
const WEEKEND_INDEXES = [5, 6];

const cloneSlots = (slots = []) =>
  (slots.length ? slots : [{ from: "", to: "", description: "" }]).map(
    (slot) => ({
      ...slot,
      id: undefined,
      break: slot.break ? { ...slot.break } : null,
    })
  );

const TIME_PARSE_FORMATS = [
  "h:mm A",
  "h:mmA",
  "hh:mm A",
  "hh:mmA",
  "HH:mm:ss",
  "HH:mm",
];

const parseSlotTime = (value) => {
  if (!value) return null;
  if (moment.isMoment(value)) return value.isValid() ? value : null;
  const raw = String(value).trim();
  const strict = moment(raw, TIME_PARSE_FORMATS, true);
  if (strict.isValid()) return strict;
  const loose12 = moment(raw, ["h:mm A", "h:mmA", "hh:mm A", "hh:mmA"], true);
  if (loose12.isValid()) return loose12;
  const loose24 = moment(raw, ["HH:mm:ss", "HH:mm"], true);
  return loose24.isValid() ? loose24 : null;
};

const formatSlotSummary = (slots = [], short = false) => {
  const parts = (slots || [])
    .filter((s) => s?.from && s?.to)
    .map((s) => {
      const from = parseSlotTime(s.from);
      const to = parseSlotTime(s.to);
      if (!from || !to) return null;
      return short
        ? `${from.format("h:mmA")}–${to.format("h:mmA")}`
        : `${from.format("h:mm A")} – ${to.format("h:mm A")}`;
    })
    .filter(Boolean);
  return parts.length ? parts.join(", ") : "";
};

const isAllDaySlots = (slots = []) =>
  slots?.length === 1 && slots[0]?.from === "00:00" && slots[0]?.to === "23:59";

const ScheduleManager = ({
  schedules,
  setSchedules,
  isProfilePage = false,
  isDarkMode = false,
  isStaffSchedule = false,
}) => {
  const themeColor = useThemeColor();
  const [activeDay, setActiveDay] = useState(0);
  const [applyTargets, setApplyTargets] = useState([]);

  // Some callers wrap setSchedules and only accept a plain array (not a React
  // updater fn). Always normalize + commit arrays so those wrappers don't store a function.
  const scheduleList = Array.isArray(schedules) ? schedules : [];

  const commitSchedules = (nextOrUpdater) => {
    if (typeof setSchedules !== "function") return;
    const next =
      typeof nextOrUpdater === "function"
        ? nextOrUpdater(scheduleList)
        : nextOrUpdater;
    setSchedules(Array.isArray(next) ? next : scheduleList);
  };

  useEffect(() => {
    if (!scheduleList.length) return;
    if (activeDay >= scheduleList.length) {
      setActiveDay(0);
    }
  }, [scheduleList, activeDay]);

  const day = scheduleList[activeDay];
  const openCount = useMemo(
    () => scheduleList.filter((s) => s.checked).length,
    [scheduleList]
  );

  const updateDay = (dayIndex, updater) => {
    commitSchedules((list) =>
      list.map((schedule, index) =>
        index === dayIndex ? updater(schedule) : schedule
      )
    );
  };

  const setDayOpen = (dayIndex, open) => {
    updateDay(dayIndex, (schedule) => ({
      ...schedule,
      checked: open,
      timeSlots:
        open &&
        (!schedule.timeSlots?.length || !schedule.timeSlots[0]?.from)
          ? [{ from: "00:00", to: "23:59", description: "" }]
          : cloneSlots(schedule.timeSlots),
    }));
  };

  const setDayAllDay = (dayIndex) => {
    updateDay(dayIndex, (schedule) => ({
      ...schedule,
      checked: true,
      timeSlots: [{ from: "00:00", to: "23:59", description: "" }],
    }));
  };

  const handleScheduleChange = (slotIndex, field, value) => {
    const formattedValue = moment.isMoment(value)
      ? value.format("HH:mm")
      : value;
    updateDay(activeDay, (schedule) => {
      const timeSlots = cloneSlots(schedule.timeSlots);
      timeSlots[slotIndex] = {
        ...timeSlots[slotIndex],
        [field]: formattedValue,
      };
      return { ...schedule, checked: true, timeSlots };
    });
  };

  const handleAddTimeSlot = () => {
    if (isStaffSchedule || isProfilePage) return;
    updateDay(activeDay, (schedule) => ({
      ...schedule,
      checked: true,
      timeSlots: [
        ...cloneSlots(schedule.timeSlots),
        { from: "", to: "", description: "", break: null },
      ],
    }));
  };

  const handleDeleteTimeSlot = (slotIndex) => {
    updateDay(activeDay, (schedule) => {
      if ((schedule.timeSlots || []).length <= 1) return schedule;
      const timeSlots = cloneSlots(schedule.timeSlots);
      timeSlots.splice(slotIndex, 1);
      return { ...schedule, timeSlots };
    });
  };

  const handleAddBreakForSlot = (slotIndex) => {
    updateDay(activeDay, (schedule) => {
      const timeSlots = cloneSlots(schedule.timeSlots);
      if (!timeSlots[slotIndex].break) {
        timeSlots[slotIndex].break = {
          startBraekTime: "",
          breakMinutes: null,
          isBreakInclusive: false,
        };
      }
      return { ...schedule, timeSlots };
    });
  };

  const handleRemoveBreakForSlot = (slotIndex) => {
    updateDay(activeDay, (schedule) => {
      const timeSlots = cloneSlots(schedule.timeSlots);
      timeSlots[slotIndex].break = null;
      return { ...schedule, timeSlots };
    });
  };

  const handleBreakChange = (slotIndex, field, value) => {
    updateDay(activeDay, (schedule) => {
      const timeSlots = cloneSlots(schedule.timeSlots);
      let formattedValue;
      if (field === "breakMinutes") {
        const val = value?.target ? value.target.value : value;
        formattedValue = val === "" ? null : parseInt(val) || null;
      } else if (field === "isBreakInclusive") {
        formattedValue = value;
      } else {
        formattedValue = moment.isMoment(value)
          ? value.format("HH:mm")
          : value;
      }
      timeSlots[slotIndex].break = {
        ...(timeSlots[slotIndex].break || {}),
        [field]: formattedValue,
      };
      return { ...schedule, timeSlots };
    });
  };

  const copyDayToIndexes = (sourceIndex, targetIndexes) => {
    commitSchedules((list) => {
      const source = list[sourceIndex];
      if (!source) return list;
      const slots = cloneSlots(source.timeSlots);
      return list.map((schedule, index) =>
        targetIndexes.includes(index)
          ? {
              ...schedule,
              checked: source.checked,
              timeSlots: slots.map((slot) => ({
                ...slot,
                break: slot.break ? { ...slot.break } : null,
              })),
            }
          : schedule
      );
    });
    setApplyTargets([]);
  };

  const toggleApplyTarget = (index) => {
    if (index === activeDay) return;
    setApplyTargets((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index]
    );
  };

  const dayMode = !day?.checked
    ? "closed"
    : isAllDaySlots(day.timeSlots)
      ? "allDay"
      : "custom";

  if (!scheduleList.length || !day) return null;

  return (
    <div
      className={`schedule-ux${isDarkMode ? " schedule-ux--dark" : ""}`}
      style={{ "--schedule-accent": themeColor }}
    >
      <div className="schedule-ux__status">
        <span className="schedule-ux__status-title">Weekly hours</span>
        <span className="schedule-ux__status-meta">
          {openCount} open · {scheduleList.length - openCount} closed
        </span>
      </div>

      {/* Week overview — pick a day to edit */}
      <div className="schedule-week" role="tablist" aria-label="Days of week">
        {scheduleList.map((schedule, index) => {
          const open = !!schedule.checked;
          const selected = index === activeDay;
          const summary = open
            ? formatSlotSummary(schedule.timeSlots, true) || "Open"
            : "Closed";

          return (
            <button
              key={schedule.day || index}
              type="button"
              role="tab"
              aria-selected={selected}
              className={[
                "schedule-week__day",
                selected ? "is-selected" : "",
                open ? "is-open" : "is-closed",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => {
                setActiveDay(index);
                setApplyTargets([]);
              }}
            >
              <span className="schedule-week__dow">
                {DAY_SHORT[index] || schedule.day?.slice(0, 3)}
              </span>
              <span className="schedule-week__hours">{summary}</span>
              <span
                className="schedule-week__dot"
                style={
                  open
                    ? { backgroundColor: themeColor }
                    : undefined
                }
              />
            </button>
          );
        })}
      </div>

      {/* Focused editor for one day */}
      <div className="schedule-editor">
        <div className="schedule-editor__header">
          <div>
            <h6 className="schedule-editor__day-name">{day.day}</h6>
            <p className="schedule-editor__hint">
              Set this day’s hours, then copy them to other days if needed.
            </p>
          </div>
        </div>

        <div className="schedule-mode" role="group" aria-label="Day mode">
          {[
            { id: "closed", label: "Closed", icon: "fa-ban" },
            { id: "allDay", label: "All day", icon: "fa-sun" },
            { id: "custom", label: "Custom hours", icon: "fa-clock" },
          ].map((mode) => (
            <button
              key={mode.id}
              type="button"
              className={`schedule-mode__btn${
                dayMode === mode.id ? " is-active" : ""
              }`}
              style={
                dayMode === mode.id
                  ? {
                      backgroundColor: themeColor,
                      borderColor: themeColor,
                    }
                  : undefined
              }
              onClick={() => {
                if (mode.id === "closed") setDayOpen(activeDay, false);
                else if (mode.id === "allDay") setDayAllDay(activeDay);
                else {
                  updateDay(activeDay, (schedule) => ({
                    ...schedule,
                    checked: true,
                    timeSlots:
                      isAllDaySlots(schedule.timeSlots) ||
                      !schedule.timeSlots?.length
                        ? [{ from: "09:00", to: "17:00", description: "" }]
                        : cloneSlots(schedule.timeSlots),
                  }));
                }
              }}
            >
              <i className={`fa ${mode.icon}`} aria-hidden="true" />
              {mode.label}
            </button>
          ))}
        </div>

        {day.checked && dayMode !== "allDay" && (
          <div className="schedule-slots">
            {(day.timeSlots || []).map((slot, slotIndex) => {
              const hasBreak = !!slot.break;
              return (
                <div
                  key={`${activeDay}-slot-${slotIndex}`}
                  className="schedule-slot-card"
                >
                  <div className="schedule-slot-card__top">
                    {(day.timeSlots || []).length > 1 && (
                      <span className="schedule-slot-card__label">
                        Period {slotIndex + 1}
                      </span>
                    )}
                    <div className="schedule-slot-card__times">
                      <MUITimePicker
                        label="Opens"
                        value={parseSlotTime(slot.from)}
                        onChange={(time) =>
                          handleScheduleChange(slotIndex, "from", time)
                        }
                        ampm={true}
                        placeholder="Opens"
                        clearable={true}
                        name={`start-time-${activeDay}-${slotIndex}`}
                      />
                      <span className="schedule-slot-card__sep">→</span>
                      <MUITimePicker
                        label="Closes"
                        value={parseSlotTime(slot.to)}
                        onChange={(time) =>
                          handleScheduleChange(slotIndex, "to", time)
                        }
                        ampm={true}
                        placeholder="Closes"
                        clearable={true}
                        name={`end-time-${activeDay}-${slotIndex}`}
                      />
                    </div>
                    <div className="schedule-slot-card__actions">
                      {slotIndex > 0 && (
                        <button
                          type="button"
                          className="schedule-text-btn schedule-text-btn--danger"
                          onClick={() => handleDeleteTimeSlot(slotIndex)}
                        >
                          Remove
                        </button>
                      )}
                      {isStaffSchedule && !hasBreak && (
                        <button
                          type="button"
                          className="schedule-text-btn"
                          style={{ color: themeColor }}
                          onClick={() => handleAddBreakForSlot(slotIndex)}
                        >
                          + Break
                        </button>
                      )}
                    </div>
                  </div>

                  {isStaffSchedule && hasBreak && (
                    <div className="schedule-break-card">
                      <div className="schedule-slot-card__times">
                        <MUITimePicker
                          label="Break starts"
                          value={
                            slot.break.startBraekTime
                              ? moment(slot.break.startBraekTime, "HH:mm")
                              : null
                          }
                          onChange={(time) =>
                            handleBreakChange(
                              slotIndex,
                              "startBraekTime",
                              time
                            )
                          }
                          ampm={true}
                          placeholder="Break start"
                          clearable={true}
                          name={`start-break-${activeDay}-${slotIndex}`}
                        />
                        <div className="schedule-break-card__minutes">
                          <label className="schedule-break-card__label">
                            Minutes
                          </label>
                          <FormInput
                            placeHolder="e.g. 30"
                            type="number"
                            min="0"
                            value={slot.break.breakMinutes || ""}
                            onchange={(e) =>
                              handleBreakChange(slotIndex, "breakMinutes", e)
                            }
                            width="100%"
                          />
                        </div>
                      </div>
                      <div className="schedule-break-card__footer">
                        <label className="schedule-break-card__check">
                          <input
                            type="checkbox"
                            checked={slot.break.isBreakInclusive || false}
                            onChange={(e) =>
                              handleBreakChange(
                                slotIndex,
                                "isBreakInclusive",
                                e.target.checked
                              )
                            }
                            style={{ accentColor: themeColor }}
                          />
                          Break counts as worked time
                        </label>
                        <button
                          type="button"
                          className="schedule-text-btn schedule-text-btn--danger"
                          onClick={() => handleRemoveBreakForSlot(slotIndex)}
                        >
                          Remove break
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {!isProfilePage && !isStaffSchedule && (
              <button
                type="button"
                className="schedule-add-slot"
                style={{ color: themeColor, borderColor: `${themeColor}66` }}
                onClick={handleAddTimeSlot}
              >
                <i className="fa fa-plus" aria-hidden="true" />
                Add another period (e.g. lunch split)
              </button>
            )}
          </div>
        )}

        {day.checked && dayMode === "allDay" && (
          <div className="schedule-allday-note">
            Open all day — <strong>12:00 AM</strong> to{" "}
            <strong>11:59 PM</strong>
          </div>
        )}

        {!day.checked && (
          <div className="schedule-closed-note">
            {day.day} is closed. Choose <strong>Custom hours</strong> or{" "}
            <strong>All day</strong> to open it.
          </div>
        )}
      </div>

      {/* Copy current day to others */}
      <div className="schedule-apply">
        <div className="schedule-apply__header">
          <span className="schedule-apply__title">
            Copy {day.day}’s hours to…
          </span>
          <div className="schedule-apply__presets">
            <button
              type="button"
              className="schedule-apply__preset"
              onClick={() => copyDayToIndexes(activeDay, WEEKDAY_INDEXES)}
            >
              Weekdays
            </button>
            <button
              type="button"
              className="schedule-apply__preset"
              onClick={() => copyDayToIndexes(activeDay, WEEKEND_INDEXES)}
            >
              Weekend
            </button>
            <button
              type="button"
              className="schedule-apply__preset"
              onClick={() =>
                copyDayToIndexes(
                  activeDay,
                  scheduleList.map((_, i) => i)
                )
              }
            >
              Every day
            </button>
          </div>
        </div>

        <div className="schedule-apply__days">
          {scheduleList.map((schedule, index) => {
            if (index === activeDay) {
              return (
                <span
                  key={schedule.day || index}
                  className="schedule-apply__pill is-source"
                  style={{
                    borderColor: themeColor,
                    color: themeColor,
                    backgroundColor: `${themeColor}18`,
                  }}
                >
                  {DAY_SHORT[index]}
                </span>
              );
            }
            const selected = applyTargets.includes(index);
            return (
              <button
                key={schedule.day || index}
                type="button"
                className={`schedule-apply__pill${
                  selected ? " is-selected" : ""
                }`}
                style={
                  selected
                    ? {
                        borderColor: themeColor,
                        backgroundColor: themeColor,
                        color: "#fff",
                      }
                    : undefined
                }
                onClick={() => toggleApplyTarget(index)}
              >
                {DAY_SHORT[index]}
              </button>
            );
          })}
        </div>

        {applyTargets.length > 0 && (
          <button
            type="button"
            className="schedule-apply__submit"
            style={{ backgroundColor: themeColor }}
            onClick={() => copyDayToIndexes(activeDay, applyTargets)}
          >
            Apply to {applyTargets.length} selected day
            {applyTargets.length > 1 ? "s" : ""}
          </button>
        )}
      </div>
    </div>
  );
};

export default ScheduleManager;
